import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
  createTournamentAPI,
  fetchCompletedTournamentsByFederationAPI,
  fetchTournamentByIdFederationAPI,
  fetchTournamentsForCreationAPI,
  fetchCompletedCategoriesByTournamentAPI,
  fetchTournamentsByFederationAndFormatAPI,
  addCategoryToTournamentAPI,
  removeCategoryFromTournamentAPI,
  deleteTournamentAPI,
  fetchTournamentByIdAPI,
  fetchCategoriesSummaryAPI,
  fetchResumenTorneosByFederationAPI ,
  fetchTorneoCategoriaAPI,
  updateTournamentAPI
} from '../../services/torneoService';
import {
  fetchTournamentMatchesAPI,
  updateMatchResultAPI,
} from '../../services/partidoService';

// Estado inicial
const initialState = {
  tournaments: [],
  completedTournamentCategories: [],
  categoriesSummary: {},
  resumen: [],
  partidos: [],
  torneoCategoria: {},
  loading: false,
  error: null,
};

const getUpdatedTournamentFromPayload = (payload = {}) =>
  payload.torneo || payload.data?.torneo || payload.data || payload;

const getTournamentId = (tournament = {}) =>
  tournament.id_torneo ?? tournament.id ?? tournament.idTorneo;

const getCompletedCategoriesFromPayload = (payload) =>
  Array.isArray(payload)
    ? payload
    : payload?.categorias || payload?.data?.categorias || [];

const mergeTournamentInState = (state, updatedTournament) => {
  const updatedTournamentId = getTournamentId(updatedTournament);

  if (updatedTournamentId === null || updatedTournamentId === undefined) {
    return;
  }

  const index = state.tournaments.findIndex(
    (tournament) => String(getTournamentId(tournament)) === String(updatedTournamentId)
  );

  if (index !== -1) {
    state.tournaments[index] = {
      ...state.tournaments[index],
      ...updatedTournament,
    };
  } else {
    state.tournaments.push(updatedTournament);
  }

  state.resumen = state.resumen.map((tournament) =>
    String(getTournamentId(tournament)) === String(updatedTournamentId)
      ? { ...tournament, ...updatedTournament }
      : tournament
  );

  if (updatedTournament.nombre_torneo && state.categoriesSummary) {
    state.categoriesSummary.nombre_torneo = updatedTournament.nombre_torneo;
  }
};

export const fetchResumenTorneosByFederation = createAsyncThunk(
  'torneo/fetchResumenByFederation',
  async (federationId, { rejectWithValue }) => {
    try {
      return await fetchResumenTorneosByFederationAPI(federationId);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);


// Thunk para llamar al endpoint
export const fetchTorneoCategoria = createAsyncThunk(
  'torneoCategoria/fetchById',
  async (idTorneoCategoria, { rejectWithValue }) => {
    try {
      return await fetchTorneoCategoriaAPI(idTorneoCategoria);
    } catch (err) {
      return rejectWithValue(err.response?.data || err.message);
    }
  }
);

// Thunk para cargar el resumen de categorías de un torneo
export const fetchCategoriesSummary = createAsyncThunk(
  'torneo/fetchCategoriesSummary',
  async ({ federationId, tournamentId }, { rejectWithValue }) => {
    try {
      const data = await fetchCategoriesSummaryAPI(federationId, tournamentId);
      return data;   // data = { categorias: [ … ] }
    } catch (err) {
      return rejectWithValue(err.message || 'Error al obtener resumen de categorías');
    }
  }
);
/**
 * Thunk para obtener un torneo específico por ID.
 */
export const fetchTournamentById = createAsyncThunk(
  'torneo/fetchTournamentById',
  async (torneoId, { rejectWithValue }) => {
    try {
      const data = await fetchTournamentByIdAPI(torneoId);
      return data; // Retorna los detalles del torneo
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener el torneo por ID');
    }
  }
);

export const fetchTournamentsByIdFederation = createAsyncThunk(
  'torneo/fetchTournaments',
  async (federationId, { rejectWithValue }) => {
    try {
      const data = await fetchTournamentByIdFederationAPI(federationId);
      //console.log('dataSlice', data);
      return data; // data es un arreglo de torneos
    } catch (error) {
      return rejectWithValue(error.message || 'Error al cargar los torneos');
    }
  }
);

export const fetchTournamentsForCreation = createAsyncThunk(
  'torneo/fetchTournamentsForCreation',
  async (federationId, { rejectWithValue }) => {
    try {
      return await fetchTournamentsForCreationAPI(federationId);
    } catch (error) {
      return rejectWithValue(error.message || 'Error al cargar los torneos');
    }
  }
);

/**
 * Thunk para agregar categorías a un torneo existente.
 */
export const addCategoryToTournament = createAsyncThunk(
  'torneo/addCategoryToTournament',
  async ({ torneoId, categoryConfig }, { rejectWithValue }) => {
    try {
      const data = await addCategoryToTournamentAPI(torneoId, categoryConfig);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error al agregar categorías al torneo');
    }
  }
);

/**
 * Thunk para eliminar una categoría de un torneo existente.
 */
export const removeCategoryFromTournament = createAsyncThunk(
  'torneo/removeCategoryFromTournament',
  async (idTorneoCategoria, { rejectWithValue }) => {
    try {
      const data = await removeCategoryFromTournamentAPI(idTorneoCategoria);
      return data; // Retorna el torneo actualizado
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error al eliminar la categoría del torneo');
    }
  }
);

/**
 * Obtener torneos por federación y formato.
 */
export const fetchTournamentsByFederationAndFormat = createAsyncThunk(
  'torneo/fetchTournamentsByFederationAndFormat',
  async ({ federationId, formato }, { rejectWithValue }) => {
    try {
      const data = await fetchTournamentsByFederationAndFormatAPI(federationId, formato);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener los torneos por formato');
    }
  }
);

// Thunk asíncrono para agregar un nuevo torneo
export const addTournament = createAsyncThunk(
  'torneo/addTournament',
  async (tournamentData, { rejectWithValue }) => {
    try {
      const response = await createTournamentAPI(tournamentData);
      //console.log(response, "slice");
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error al crear el torneo');
    }
  }
);

export const updateTournament = createAsyncThunk(
  'torneo/updateTournament',
  async ({ torneoId, data }, { rejectWithValue }) => {
    try {
      return await updateTournamentAPI(torneoId, data);
    } catch (error) {
      return rejectWithValue(
        error.response?.data || error.message || 'Error al actualizar el torneo'
      );
    }
  }
);

// Thunk asíncrono para obtener partidos de un torneo
export const fetchTournamentMatches = createAsyncThunk(
  'torneo/fetchTournamentMatches',
  async (tournamentId, { rejectWithValue }) => {
    try {
      const matches = await fetchTournamentMatchesAPI(tournamentId);
      //console.log(matches, "//console.log matches")
      return { tournamentId, matches };
    } catch (error) {
      return rejectWithValue(error.message || 'Error al cargar los partidos del torneo');
    }
  }
);


// Thunk asíncrono para obtener torneos completados por ID de federación
export const fetchCompletedCategoriesByTournament = createAsyncThunk(
  'torneo/fetchCompletedCategoriesByTournament',
  async ({federationId,torneoId}, { rejectWithValue }) => {
    try {
      const data = await fetchCompletedCategoriesByTournamentAPI(federationId,torneoId);
      //console.log('Completed Tournaments:', data);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener torneos completados');
    }
  }
);

// Thunk asíncrono para obtener torneos completados por ID de federación
export const fetchCompletedTournamentsByFederation = createAsyncThunk(
  'torneo/fetchCompletedTournaments',
  async (federationId, { rejectWithValue }) => {
    try {
      const data = await fetchCompletedTournamentsByFederationAPI(federationId);
      //console.log('Completed Tournaments:', data);
      return data;
    } catch (error) {
      return rejectWithValue(error.message || 'Error al obtener torneos completados');
    }
  }
);

// Thunk asíncrono para actualizar resultados de un partido
export const updateMatchResult = createAsyncThunk(
  'torneo/updateMatchResult',
  async ({ tournamentId, matchId, results }, { rejectWithValue }) => {
    try {
      const updatedMatch = await updateMatchResultAPI(tournamentId, matchId, results);
      return { tournamentId, match: updatedMatch };
    } catch (error) {
      return rejectWithValue(error.message || 'Error al actualizar el partido');
    }
  }
);

export const deleteTournament = createAsyncThunk(
  'torneo/deleteTournament',
  async (idTorneo, { rejectWithValue }) => {
    try {
      const data = await deleteTournamentAPI(idTorneo);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Error al eliminar el torneo');
    }
  }
);

const torneoSlice = createSlice({
  name: 'torneo',
  initialState,
  reducers: {
    clearTournamentState(state) {
      state.tournaments = [];
      state.completedTournamentCategories = [];
      state.loading = false;
      state.categoriesSummary = {};
      state.error = null;
      state.resumen = [];
    },
  },
  extraReducers: (builder) => {
    builder
      // Manejo de fetchTournamentById
      .addCase(fetchTournamentById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTournamentById.fulfilled, (state, action) => {
        const updatedTournament = action.payload;
        const index = state.tournaments.findIndex(
          (t) => t.id_torneo === updatedTournament.id || t.id_torneo === updatedTournament.id_torneo
        );
        if (index !== -1) {
          state.tournaments[index] = updatedTournament;
        } else {
          // Si el torneo no existe en el estado, lo añadimos
          state.tournaments.push(updatedTournament);
        }
        state.loading = false;
      })
      .addCase(fetchTournamentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Manejo de fetchTournamentsByIdFederation
      .addCase(fetchTournamentsByIdFederation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTournamentsByIdFederation.fulfilled, (state, action) => {
        state.tournaments = action.payload;
        state.loading = false;
      })
      .addCase(fetchTournamentsByIdFederation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTournamentsForCreation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTournamentsForCreation.fulfilled, (state, action) => {
        state.tournaments = action.payload;
        state.loading = false;
      })
      .addCase(fetchTournamentsForCreation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // casos para fetchCategoriesSummary
     .addCase(fetchCategoriesSummary.pending, state => {
         state.loading = true;
         state.error = null;
       })
       .addCase(fetchCategoriesSummary.fulfilled, (state, action) => {
         state.categoriesSummary = action.payload;
         state.loading = false;
       })
       .addCase(fetchCategoriesSummary.rejected, (state, action) => {
         state.loading = false;
         state.error = action.payload;
       })

      // Manejo de fetchCompletedTournamentsByFederation
      .addCase(fetchCompletedTournamentsByFederation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompletedTournamentsByFederation.fulfilled, (state, action) => {
        state.tournaments = action.payload;
        state.completedTournamentCategories = [];
        state.loading = false;
      })
      .addCase(fetchCompletedTournamentsByFederation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(fetchCompletedCategoriesByTournament.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.completedTournamentCategories = [];
      })
      .addCase(fetchCompletedCategoriesByTournament.fulfilled, (state, action) => {
        state.completedTournamentCategories = getCompletedCategoriesFromPayload(
          action.payload
        );
        state.loading = false;
      })
      .addCase(fetchCompletedCategoriesByTournament.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Manejo de addTournament
      .addCase(addTournament.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addTournament.fulfilled, (state, action) => {
        state.loading = false;
        state.tournaments.push({
          ...action.payload,
          setsPerCategory: action.payload.setsPerCategory || [],
          standings: [],
        });
      })
      .addCase(addTournament.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateTournament.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTournament.fulfilled, (state, action) => {
        state.loading = false;
        mergeTournamentInState(
          state,
          getUpdatedTournamentFromPayload(action.payload)
        );
      })
      .addCase(updateTournament.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Manejo de fetchTournamentMatches
      .addCase(fetchTournamentMatches.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTournamentMatches.fulfilled, (state, action) => {
        state.partidos= action.payload;
        state.loading = false;
      })
      .addCase(fetchTournamentMatches.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchTournamentsByFederationAndFormat.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTournamentsByFederationAndFormat.fulfilled, (state, action) => {
        state.tournaments = action.payload;
        state.loading = false;
      })
      .addCase(fetchTournamentsByFederationAndFormat.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addCategoryToTournament.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addCategoryToTournament.fulfilled, (state, action) => {
        state.loading = false;
        const updatedTournament = action.payload.torneo;
        const index = state.tournaments.findIndex(t => t.id_torneo === updatedTournament.id_torneo);
        if (index !== -1) {
          state.tournaments[index] = updatedTournament;
        }
      })
      .addCase(addCategoryToTournament.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Manejo de removeCategoryFromTournament
      .addCase(removeCategoryFromTournament.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeCategoryFromTournament.fulfilled, (state, action) => {
        const updatedTournament = action.payload.torneo;
        const index = state.tournaments.findIndex(t => t.id_torneo === updatedTournament.id_torneo);
        if (index !== -1) {
          state.tournaments[index] = updatedTournament; // Actualiza el torneo con las categorías restantes
        }
        state.loading = false;
      })
      .addCase(removeCategoryFromTournament.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteTournament.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTournament.fulfilled, (state, action) => {
        const deletedTournamentId =
          action.meta?.arg ||
          action.payload?.id_torneo ||
          action.payload?.id ||
          action.payload?.torneo?.id_torneo;

        state.tournaments = state.tournaments.filter(
          (tournament) =>
            String(tournament.id_torneo) !== String(deletedTournamentId)
        );
        state.loading = false;
      })
      .addCase(deleteTournament.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchResumenTorneosByFederation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResumenTorneosByFederation.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.resumen = payload;
      })
      .addCase(fetchResumenTorneosByFederation.rejected, (state, { payload, error }) => {
        state.loading = false;
        state.error = payload || error.message;
      })
      .addCase(fetchTorneoCategoria.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTorneoCategoria.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.torneoCategoria = payload;
      })
      .addCase(fetchTorneoCategoria.rejected, (state, { payload, error }) => {
        state.loading = false;
        state.error = payload || error.message;
      });
  },
});

export const { clearTournamentState } = torneoSlice.actions;

export default torneoSlice.reducer;
